import { useEffect, useRef, useState } from "react";
import { BasketballTrail } from "./BasketballTrail";
import { COURT_DIMENSIONS } from "./courtConstants";
import { courtToWorld, normalizeToWorldSpace } from "./courtUtils";
import { ParticleSystem } from "./ParticleSystem";

export const AnimatedBasketball = ({
	shot,
	delay = 0,
	animationState,
	onComplete,
}) => {
	const basketballRef = useRef();
	const [showSwooshEffect, setShowSwooshEffect] = useState(false);
	const [trailPoints, setTrailPoints] = useState([]);
	const [showIndicator, setShowIndicator] = useState(false);
	const [ballOpacity, setBallOpacity] = useState(1);

	const animationFrameId = useRef();
	const startTimeRef = useRef(0);
	const progressRef = useRef(0);
	const startedRef = useRef(false);
	const completedRef = useRef(false);
	const delayTimeoutRef = useRef(null);
	const delayRemainingRef = useRef(delay * 1000);
	const delayStartRef = useRef(0);
	const shotDuration = 800;
	const bounceDuration = 600;
	const fallDuration = 400; // Duration for scored shots to fall through net

	const basketPos = courtToWorld(
		COURT_DIMENSIONS.WIDTH / 2,
		COURT_DIMENSIONS.BASKET_POSITION_FROM_BASELINE,
	);

	// Normalize to world space (handles both old and new formats)
	const worldLocation = normalizeToWorldSpace(shot.location);
	const startPos = {
		x: worldLocation.x,
		z: worldLocation.z,
	};

	// Determine shot type based on result
	const shotType = shot.field_goal_result || "miss";
	const isMiss = shotType === "miss";
	const totalDuration = isMiss
		? shotDuration + bounceDuration
		: shotDuration + fallDuration;

	// Compute random trajectory values once per mount.
	// Component is keyed (key={`animation-${shot.id}-${restartKey}`}),
	// so it remounts for new shots — safe to use lazy ref init.
	const trajectoryRef = useRef(null);
	if (!trajectoryRef.current) {
		const randomSign = Math.random() > 0.5 ? 1 : -1;
		trajectoryRef.current = {
			endPos:
				shot.field_goal_result === "score"
					? [basketPos.x, COURT_DIMENSIONS.BASKET_HEIGHT - 0.2, basketPos.z]
					: [
							basketPos.x + Math.random() * 0.3,
							COURT_DIMENSIONS.BASKET_HEIGHT,
							basketPos.z + (Math.random() + 0.1) * 0.3 * randomSign,
						],
			bounceEndPos: [
				basketPos.x + (Math.random() - 0.5) * 6.5, // Left/right bounce
				0.4, // Floor
				basketPos.z + Math.random() * 6.5, // Always bounce away from backboard
			],
			fallEndPos: [
				basketPos.x,
				0.4, // Floor level
				basketPos.z,
			],
		};
	}
	const { endPos, bounceEndPos, fallEndPos } = trajectoryRef.current;

	// Store animate/startAfterDelay in refs so effects can call them
	// without including them as dependencies (avoids re-render → effect cycle)
	const animateFnRef = useRef();
	const startAfterDelayFnRef = useRef();

	const updateTrajectory = (progress) => {
		const x = startPos.x + (endPos[0] - startPos.x) * progress;
		const z = startPos.z + (endPos[2] - startPos.z) * progress;

		// Enhanced parabolic height with proper physics
		const distance = Math.sqrt(
			(endPos[0] - startPos.x) ** 2 + (endPos[2] - startPos.z) ** 2,
		);
		const arcHeight = Math.max(3.5, distance * 0.4);

		// Physics-based trajectory
		const t = progress;
		const y = 0.12 + 4 * arcHeight * t * (1 - t) + endPos[1] * t * t;

		return { x, y, z };
	};

	const animate = (timestamp) => {
		if (!startTimeRef.current) {
			// Subtract the time that has already passed if resuming
			startTimeRef.current = timestamp - progressRef.current * totalDuration;
		}

		const elapsed = timestamp - startTimeRef.current;
		const totalProgress = Math.min(elapsed / totalDuration, 1);
		progressRef.current = totalProgress;

		let x, y, z;

		if (elapsed < shotDuration) {
			// Phase 1: Shot trajectory
			const shotProgress = Math.min(elapsed / shotDuration, 1);
			({ x, y, z } = updateTrajectory(shotProgress));
		} else if (!isMiss && elapsed < totalDuration) {
			// Phase 2: Fall trajectory (Score only - straight down through net)
			const fallProgress = Math.min((elapsed - shotDuration) / fallDuration, 1);

			// Keep X and Z constant (straight down)
			x = endPos[0];
			z = endPos[2];

			// Accelerating fall for Y (gravity effect)
			y =
				endPos[1] + (fallEndPos[1] - endPos[1]) * (fallProgress * fallProgress);

			// Fade out during fall
			setBallOpacity(1 - fallProgress);
		} else if (isMiss && elapsed < totalDuration) {
			// Phase 2: Bounce trajectory (Miss only) - Realistic projectile motion
			const bounceProgress = Math.min(
				(elapsed - shotDuration) / bounceDuration,
				1,
			);

			// Linear horizontal motion (constant velocity in X and Z)
			x = endPos[0] + (bounceEndPos[0] - endPos[0]) * bounceProgress;
			z = endPos[2] + (bounceEndPos[2] - endPos[2]) * bounceProgress;

			// Parabolic arc for Y (realistic projectile motion after rim bounce)
			// Ball bounces up slightly then falls down following physics
			const bounceDistance = Math.sqrt(
				(bounceEndPos[0] - endPos[0]) ** 2 + (bounceEndPos[2] - endPos[2]) ** 2,
			);
			const bounceArcHeight = Math.min(1.8, bounceDistance * 0.3); // Arc height based on distance

			// Projectile motion formula: creates parabolic arc from rim to floor
			const t = bounceProgress;
			y =
				endPos[1] +
				4 * bounceArcHeight * t * (1 - t) +
				(bounceEndPos[1] - endPos[1]) * t * t;

			setBallOpacity(1 - bounceProgress);
		} else {
			// Animation Complete
			if (isMiss) {
				x = bounceEndPos[0];
				y = bounceEndPos[1];
				z = bounceEndPos[2];
			} else {
				x = fallEndPos[0];
				y = fallEndPos[1];
				z = fallEndPos[2];
			}
			setBallOpacity(0);
		}

		if (basketballRef.current) {
			basketballRef.current.position.set(x, y, z);
			basketballRef.current.rotation.x += 0.18;
			basketballRef.current.rotation.z += 0.12;
		}

		setTrailPoints((prev) => {
			const newTrail = [...prev, [x, y, z]];
			return newTrail.length > 15 ? newTrail.slice(-15) : newTrail;
		});

		if (totalProgress < 1) {
			animationFrameId.current = requestAnimationFrame((ts) =>
				animateFnRef.current(ts),
			);
		} else {
			// Handle animation end
			if (!isMiss) {
				setShowSwooshEffect(true);
				setTimeout(() => setShowSwooshEffect(false), 300);
			}

			setTimeout(() => {
				if (basketballRef.current) basketballRef.current.visible = false;
				setTrailPoints([]);
				if (!completedRef.current) {
					completedRef.current = true;
					if (typeof onComplete === "function") onComplete();
				}
			}, 400);
		}
	};

	const startAfterDelay = () => {
		if (basketballRef.current) {
			basketballRef.current.visible = true;
			basketballRef.current.position.set(startPos.x, 0.12, startPos.z);
		}
		setBallOpacity(1); // Reset opacity for new animation
		setShowIndicator(true);
		const indicatorTimer = setTimeout(() => setShowIndicator(false), 500);
		startedRef.current = true;
		animationFrameId.current = requestAnimationFrame((ts) =>
			animateFnRef.current(ts),
		);
		return () => clearTimeout(indicatorTimer);
	};

	// Keep refs pointing to latest closures
	animateFnRef.current = animate;
	startAfterDelayFnRef.current = startAfterDelay;

	// Effect 1: Start delay timer on mount (runs once — component is keyed)
	useEffect(() => {
		delayRemainingRef.current = delay * 1000;
		delayStartRef.current = performance.now();
		delayTimeoutRef.current = setTimeout(() => {
			startAfterDelayFnRef.current();
		}, delayRemainingRef.current);

		return () => {
			if (delayTimeoutRef.current) clearTimeout(delayTimeoutRef.current);
			if (animationFrameId.current)
				cancelAnimationFrame(animationFrameId.current);
		};
	}, [delay]);

	// Effect 2: Handle pause/resume (runs only on animationState change)
	useEffect(() => {
		if (animationState === "paused") {
			// Pause delayed start
			if (!startedRef.current && delayTimeoutRef.current) {
				clearTimeout(delayTimeoutRef.current);
				delayTimeoutRef.current = null;
				delayRemainingRef.current = Math.max(
					0,
					delayRemainingRef.current -
						(performance.now() - delayStartRef.current),
				);
			}
			// Pause animation in progress
			if (animationFrameId.current) {
				cancelAnimationFrame(animationFrameId.current);
				animationFrameId.current = null;
				startTimeRef.current = 0;
			}
		} else if (animationState === "playing") {
			// Resume delayed start
			if (
				!startedRef.current &&
				delayRemainingRef.current > 0 &&
				!delayTimeoutRef.current
			) {
				delayStartRef.current = performance.now();
				delayTimeoutRef.current = setTimeout(() => {
					startAfterDelayFnRef.current();
				}, delayRemainingRef.current);
			}
			// Resume animation if it had started and not completed
			if (
				startedRef.current &&
				progressRef.current < 1 &&
				!animationFrameId.current
			) {
				animationFrameId.current = requestAnimationFrame((ts) =>
					animateFnRef.current(ts),
				);
			}
		}
	}, [animationState]);

	const getShotColor = () => {
		if (shotType === "score") return "#00FF00";
		if (shotType === "miss") return "#A5A5A5";
		return "#FFA500";
	};

	return (
		<group>
			<mesh ref={basketballRef} castShadow visible={false}>
				<sphereGeometry args={[0.2, 20, 20]} />
				<meshStandardMaterial
					color="#D2691E"
					roughness={0.9}
					metalness={0.05}
					normalScale={[0.5, 0.5]}
					transparent={true}
					opacity={ballOpacity}
				/>
			</mesh>

			<BasketballTrail points={trailPoints} color={getShotColor()} />

			<mesh
				position={[startPos.x, 0.05, startPos.z]}
				rotation={[-Math.PI / 2, 0, 0]}
			>
				<circleGeometry args={[0.4, 32]} />
				<meshStandardMaterial
					color={getShotColor()}
					transparent
					opacity={showIndicator ? 0.3 : 0}
					emissive={getShotColor()}
					emissiveIntensity={showIndicator ? 0.3 : 0}
				/>
			</mesh>

			{showSwooshEffect && (
				<ParticleSystem
					position={endPos}
					color="#ffffff"
					count={6}
					active={showSwooshEffect}
					paused={animationState === "paused"}
				/>
			)}
		</group>
	);
};
